import StatusPill, { type Tone } from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatNumber, humanize } from '@/lib/format';
import { admin_account_path, new_admin_account_path } from '@/rails/routes';
import type { Account } from '@/types/platform';
import { Link } from '@inertiajs/react';

interface Props {
  accounts: Account[];
}

const SOURCE_LABELS: Record<Account['source'], string> = {
  caprock_client: 'Client job',
  public: 'Public signup',
};

const SUBSCRIPTION_TONES: Record<string, Tone> = {
  active: 'success',
  trialing: 'info',
  past_due: 'danger',
  incomplete: 'warning',
  canceled: 'neutral',
};

const Index = ({ accounts }: Props) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Accounts"
        description="Every company on the platform: client jobs and public signups."
        actions={
          <Button asChild>
            <Link href={new_admin_account_path()}>New client</Link>
          </Button>
        }
      />

      <Card className="py-2">
        <CardContent className="px-2">
          {accounts.length === 0 ? (
            <EmptyState
              className="px-4"
              description="No accounts yet. A new client or a public signup shows up here."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Apps</TableHead>
                  <TableHead className="text-right">Members</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">
                      <Link
                        href={admin_account_path(account.id)}
                        className="underline-offset-4 hover:underline"
                      >
                        {account.name}
                      </Link>
                    </TableCell>
                    <TableCell>{SOURCE_LABELS[account.source]}</TableCell>
                    <TableCell>{account.plan.name}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(account.appCount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(account.memberCount)}
                    </TableCell>
                    <TableCell>
                      <BillingState account={account} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(account.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// A paid plan with no running subscription is billed outside checkout.
const BillingState = ({ account }: { account: Account }) => {
  if (account.comped) return <StatusPill>Comped</StatusPill>;

  if (account.subscriptionStatus) {
    return (
      <StatusPill tone={SUBSCRIPTION_TONES[account.subscriptionStatus] ?? 'neutral'}>
        {humanize(account.subscriptionStatus)}
      </StatusPill>
    );
  }

  return <span className="text-muted-foreground">—</span>;
};

export default Index;
